// The contract steps of the psychotherapy variant.
//
// The server decides WHICH documents the signature request holds and in which
// order (server/src/services/ptDocs/registry.ts documentsFor) and hands the
// order over at link exchange as contract keys per insurance type. This module
// turns those keys into the ContractConfig the contract step renders: privacy
// and billing consent show under the org's own contract texts, the
// psychotherapy documents are defined here.
//
// consentType of Dok. 02/02b is 'treatment_contract': it REPLACES the
// onboarding's treatment contract (Paul, 2026-10-28), so every consumer of
// that consent (submission, Make, Goya) keeps working unchanged.
import type { ContractConfig } from '../context/TenantContext'

export const PT_CONTRACTS: Record<string, ContractConfig> = {
  pt_behandlungsvertrag: {
    key: 'pt_behandlungsvertrag',
    documentIndex: -1,
    consentType: 'treatment_contract',
    downloadFilename: 'Behandlungsvertrag.pdf',
    title: { de: 'Behandlungsvertrag', en: 'Treatment contract' },
    description: {
      de: 'Der Behandlungsvertrag für deine Psychotherapie mit wirtschaftlicher Aufklärung: Die probatorischen Sitzungen sind privat, die Kurzzeittherapie gilt vorbehaltlich der Bewilligung durch deine Krankenkasse, und ein nicht rechtzeitig abgesagter Termin wird mit dem Honorar der Sitzung berechnet.',
      en: 'The treatment contract for your psychotherapy, including the financial information: the trial sessions are private, short-term therapy is subject to approval by your health insurer, and an appointment not cancelled in time is charged at the session fee.',
    },
    downloadLabel: { de: 'Behandlungsvertrag herunterladen', en: 'Download treatment contract' },
  },
  pt_behandlungsvertrag_selbstzahler: {
    key: 'pt_behandlungsvertrag_selbstzahler',
    documentIndex: -1,
    consentType: 'treatment_contract',
    downloadFilename: 'Behandlungsvertrag.pdf',
    title: { de: 'Behandlungsvertrag', en: 'Treatment contract' },
    description: {
      de: 'Der Behandlungsvertrag für deine Psychotherapie als Selbstzahler:in: das Honorar je Sitzung, wann wir abrechnen, und dass ein nicht rechtzeitig abgesagter Termin mit dem Honorar der Sitzung berechnet wird.',
      en: 'The treatment contract for your psychotherapy as a self-paying patient: the fee per session, when we invoice, and that an appointment not cancelled in time is charged at the session fee.',
    },
    downloadLabel: { de: 'Behandlungsvertrag herunterladen', en: 'Download treatment contract' },
  },
  pt_patientenerklaerung: {
    key: 'pt_patientenerklaerung',
    documentIndex: -1,
    consentType: 'pt_patientenerklaerung',
    downloadFilename: 'Patientenerklaerung.pdf',
    title: { de: 'Patientenerklärung', en: 'Patient declaration' },
    description: {
      de: 'Deine Vollmacht für uns, die Entbindung von der Schweigepflicht gegenüber den Stellen, die für die Kostenübernahme nötig sind, und deine Einwilligung in die Datenübermittlung.',
      en: 'Your power of attorney for us, the release from confidentiality towards the parties needed for the cost coverage, and your consent to the data transfer.',
    },
    downloadLabel: { de: 'Patientenerklärung herunterladen', en: 'Download patient declaration' },
  },
}

export const isPtContractKey = (key: string): boolean => key in PT_CONTRACTS

/**
 * The contract keys for the patient's insurance type. Before the insurance step the type is unknown:
 * the statutory list then stands in (every list has the same length — only the contract differs).
 * `fallback` is the single list of a session exchanged before 2026-10-29.
 */
export function ptContractKeysFor(
  byInsurance: Record<string, string[]> | null | undefined,
  insuranceType: string,
  fallback: string[] | null | undefined,
): string[] {
  if (byInsurance) return byInsurance[insuranceType] ?? byInsurance.public ?? []
  return fallback ?? []
}

/**
 * The contract steps for the server's key order. A key that is neither one of ours nor in the
 * org's config makes the list unusable → [] (the caller then runs the standard flow).
 */
export function resolvePtContracts(keys: string[] | null | undefined, orgContracts: ContractConfig[]): ContractConfig[] {
  if (!Array.isArray(keys) || keys.length === 0) return []
  const out: ContractConfig[] = []
  for (const key of keys) {
    const c = PT_CONTRACTS[key] ?? orgContracts.find(o => o.key === key || o.consentType === key)
    if (!c) return []
    out.push(c)
  }
  return out
}
